<template>
  <div :class="['fpm--modal', verticalAlignClass, horizontalAlignClass]" :style="modalStyles" v-if="exist" v-show="toggleModal">
    <transition appear name="fade">
      <div class="fpm--modal-overlay" :style="overlayStyles" @click.self.stop="overlayOnClick" v-show="visible"></div>
    </transition>
    <transition appear @after-enter="afterOpenModal" @before-leave="beforeCloseModalAnimation" @after-leave="afterCloseModalAnimation" :name="animation">
      <div class="fpm--modal-content" :style="computedContentStyles" v-show="visible">
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script>
function preventAnimationSideEffect (options = {}) {
  const { restore } = options
  if (restore) {
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
    return
  }
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overflow = 'hidden'
}

export default {
  props: {
    value: {
      type: Boolean,
      required: true
    },
    index: {
      type: Number
    },
    modals: {
      type: Array
    },
    fpmId: {
      type: [Number, String]
    },
    isBottomMost: {
      type: Boolean
    },
    maxHeight: {
      type: [Number, String],
      default: '80%'
    },
    height: {
      type: [Number, String]
    },
    width: {
      type: [Number, String],
      default: 800
    },
    backgroundColor: {
      type: String,
      default: '#fff'
    },
    clickToClose: {
      type: Boolean,
      default: true
    },
    escToClose: {
      type: Boolean,
      default: true
    },
    placement: {
      type: String,
      default: 'center bottom'
    },
    modalStyles: {
      type: [String, Object, Array]
    },
    overlayStyles: {
      type: [String, Object, Array]
    },
    contentStyles: {
      type: [String, Object, Array]
    },
    animation: {
      type: String,
      default: 'bounce-up'
    },
    // ID of the main app root element to apply scroll-lock on
    // e.g. 'app' if your main Vue mounts on <div id="app">
    rootAppId: {
      type: String,
      default: 'app'
    }
  },
  computed: {
    marginTop: function () {
      if (typeof this.maxHeight === 'number') {
        return `calc(100vh - ${this.maxHeight}px)`
      }
      if (this.maxHeight.includes('%')) {
        const marginTopPercentage = 100 - Number(this.maxHeight.replace('%', ''))
        return `${marginTopPercentage}vh`
      }
      return `calc(100vh - ${this.maxHeight})`
    },
    boxShadow: function () {
      const verticalAlign = this.placement.split(' ')[1]
      if (verticalAlign === 'bottom') {
        return `0 10px 0 0 ${this.backgroundColor}`
      }
      return ''
    },
    convertedHeight: function () {
      if (typeof this.height === 'number') {
        return `${this.height}px`
      }
      return this.height
    },
    convertedWidth: function () {
      if (typeof this.width === 'number') {
        return `${this.width}px`
      }
      return this.width
    },
    verticalAlignClass: function () {
      const verticalAlign = this.placement.split(' ')[1]
      return `fpm--align-${verticalAlign}`
    },
    horizontalAlignClass: function () {
      const horizontalAlign = this.placement.split(' ')[0]
      return `fpm--justify-${horizontalAlign}`
    },
    computedContentStyles: function () {
      if (typeof this.contentStyles === 'string') {
        return `margin-top: ${this.marginTop}; background-color: ${this.backgroundColor}; box-shadow: ${this.boxShadow}; width: ${this.convertedWidth}; height: ${this.convertedHeight}; ${this.contentStyles}`
      }

      if (Array.isArray(this.contentStyles)){
        return [{ marginTop: this.marginTop, backgroundColor: this.backgroundColor, boxShadow: this.boxShadow, width: this.convertedWidth, height: this.convertedHeight }].concat(this.contentStyles)
      }

      return [{ marginTop: this.marginTop, backgroundColor: this.backgroundColor, boxShadow: this.boxShadow, width: this.convertedWidth, height: this.convertedHeight }, this.contentStyles]
    },
    isModalAtTop: function(){
      return this.index === this.modals.length - 1
    }
  },
  created: function () {
    window.addEventListener('keyup', this.closeIfEsc)
    this.$FModal.eventInterface.$on('hide-dynamic', this.hideDynamicCallback)
  },
  mounted: function () {
    if (this.value) {
      setTimeout(() => {
        this.openModal()
      }, 17)
    }
  },
  beforeDestroy: function () {
    window.removeEventListener('keyup', this.closeIfEsc)
    this.$FModal.eventInterface.$off('hide-dynamic', this.hideDynamicCallback)
  },
  destroyed() {
    preventAnimationSideEffect({ restore: true })
  },
  data: function () {
    return {
      exist: this.value,
      toggleModal: false,
      visible: false,
    }
  },
  methods: {
    hideDynamicCallback: function (fpmId) {
      if (fpmId === undefined) return
      if (this.fpmId === fpmId) this.visible = false
    },
    closeIfEsc: function (event) {
      if (!this.escToClose) return
      if (!this.isModalAtTop) return
      if (event.keyCode === 27) {
        this.visible = false
      }
    },
    overlayOnClick: function () {
      this.$emit('overlay-clicked', this.fpmId)
      if (!this.clickToClose) return
      this.visible = false
    },
    afterOpenModal: function () {
      preventAnimationSideEffect({ restore: true })
    },
    beforeCloseModalAnimation: function () {
      // nem kell semmi, position: fixed modal nem tolja el a layoutot
    },
    afterCloseModalAnimation: function () {
      preventAnimationSideEffect({ restore: true })
      this.closeModal()
    },
    openModal: function () {
      preventAnimationSideEffect()
      this.toggleModal = this.value
      this.visible = this.value
    },
    closeModal: function () {
      this.toggleModal = false
      this.exist = false
      this.$emit('input', false)
      this.$emit('modal-closed', this.fpmId)
      this.$FModal.eventInterface.$emit('modal-closed', this.fpmId)
    }
  }
}
</script>

<style>
.fpm--modal {
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  min-height: 100vh;
  width: 100%;
  z-index: 60000;
}

/* placement */
.fpm--modal.fpm--align-bottom {
  align-items: flex-end;
}

.fpm--modal.fpm--align-center {
  align-items: center;
}

.fpm--modal.fpm--align-top {
  align-items: flex-start;
}

.fpm--modal.fpm--justify-left {
  justify-content: flex-start;
}

.fpm--modal.fpm--justify-center {
  justify-content: center;
}

.fpm--modal.fpm--justify-right {
  justify-content: flex-end;
}

.fpm--modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(128, 128, 128, 0.3);
  z-index: 60001;
}

.fpm--modal-content {
  position: relative;
  max-width: 100%;
  border-radius: 4px;
  z-index: 60002;
}

/* Animation */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s;
}
.fade-enter,
.fade-leave-to {
  opacity:0;
}

.bounce-up-enter-active {
  animation: bounce-up 0.4s;
}
.bounce-up-leave-active {
  animation: bounce-up 0.4s reverse;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition:all 0.4s;
}
.fade-up-enter,
.fade-up-leave-to {
  opacity:0;
  transform:translateY(10px);
}

@keyframes bounce-up {
  0% {
    opacity: 0;
    transform: translateY(50px);
  }
  60% {
    opacity: 1;
    transform: translateY(-7px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
